import DeleteIcon from '@mui/icons-material/Delete'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import useTheme from '@mui/material/styles/useTheme'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useFormik } from 'formik'
import { enqueueSnackbar } from 'notistack'
import { ReactNode, SyntheticEvent, useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import Stack from '@mui/material/Stack'
import { useSessionContext } from '@providers/session'
import { buildErrorMessage } from '@shared/error/error'
import { i18WithParams as t } from '@shared/locale'
import { captureError } from '@shared/monitoring'
import { useRoutes } from 'app/use_routes'
import { ButtonActions } from 'components-ui/src/button_actions/button_actions'
import { NavBlocker } from 'components-ui/src/nav_blocker/nav_blocker'
import { isEmpty } from 'components-ui/src/shared'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { TextInputPhone } from 'components-ui/src/textinput-phone/textinput-phone'
import { Hooks } from 'service-api'
import { ClientLocationAssociatedContactService } from 'service-api/src/services/client_location_associated_contacts.service'
import { AssociatedContactClientDto, AssociatedContactCreateInput } from 'service-api/src/types/api'
import { DeleteDialog } from './delete_dialog'
import { contactSchema, contactTypeOptions, setupInitialValues, TContactSchema } from './schema'

const ContactFormWrapper = ({ children }: { children: ReactNode }) => {
  const { navigateRoute } = useRoutes()

  const btnBackHandler = () => navigateRoute('profileContacts', {})

  return (
    <Box sx={{ width: '100%' }}>
      <Button variant="text" onClick={btnBackHandler} startIcon={<KeyboardArrowLeftIcon />}>
        {t('profile.contacts.backLink')}
      </Button>
      <Spacer height={2} />
      {children}
    </Box>
  )
}

interface TContactEditFormProps {
  contact?: AssociatedContactClientDto
  onFormState: (isDirty: boolean) => void
}

const valueOrNull = (value?: string) => (isEmpty(value) ? null : value!.trim())

export const ContactEditForm = ({ contact, onFormState }: TContactEditFormProps) => {
  const { accountName: locationName = '' } = useParams()
  const sessionCtx = useSessionContext()
  const { navigateRoute } = useRoutes()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const lupId = sessionCtx.state.user!.lupId

  const formik = useFormik<TContactSchema>({
    initialValues: setupInitialValues(contact),
    validationSchema: contactSchema,
    onSubmit: async (values): Promise<boolean> => {
      const payload = {
        displayName: values.name,
        description: valueOrNull(values.description),
        phone: values.phone,
        type: values.type.id,
        locationUserProfileId: lupId,
      } as AssociatedContactCreateInput

      try {
        if (contact) {
          await ClientLocationAssociatedContactService.update(locationName, lupId, contact.id || '', payload)
        } else {
          await ClientLocationAssociatedContactService.create(locationName, lupId, payload)
        }
      } catch (error) {
        captureError(error as Error)
        enqueueSnackbar(buildErrorMessage(error), { variant: 'error' })
        return false
      }

      formik.resetForm({ values })
      setTimeout(() => navigateRoute('profileContacts', {}), 300)
      enqueueSnackbar(t('profile.update.success'), { variant: 'success' })
      return true
    },
  })

  useEffect(() => onFormState(formik.dirty), [formik.dirty, onFormState])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onChangeSelect = (_ev: SyntheticEvent, field: string, value: any) => {
    formik.setFieldValue(field, value)
  }

  const onSubmitHandler = async () => await formik.submitForm()
  const onCancelHandler = async () => {
    if (formik.dirty) {
      formik.resetForm()
      return
    }

    setTimeout(() => navigateRoute('profileContacts', {}))
    return
  }
  const deleteContactHandler = async (contact: AssociatedContactClientDto) => {
    if (!contact.id) {
      return
    }

    try {
      await ClientLocationAssociatedContactService.remove(locationName, lupId, contact.id)

      formik.resetForm()
      // Timeout to let form reset settle and not be catch by NavBlocker
      setTimeout(() => navigateRoute('profileContacts', {}), 300)
    } catch (error) {
      captureError(error as Error)
      enqueueSnackbar(buildErrorMessage(error), { variant: 'error' })
    }
  }

  const title = contact ? t('profile.contacts.editContact') : t('profile.contacts.addContact')

  return (
    <>
      <Paper elevation={1} sx={{ p: 2, borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
        <Typography variant="h5">{title}</Typography>
        <Spacer height={3} />
        <TextField
          id="name"
          label={t('profile.contacts.form.displayName')}
          variant="outlined"
          sx={{ flex: 1, mb: 4 }}
          value={formik.values.name}
          onChange={formik.handleChange}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          fullWidth
        />
        <Autocomplete
          id="type"
          sx={{ flex: 1 }}
          includeInputInList
          clearIcon={null}
          options={contactTypeOptions}
          isOptionEqualToValue={(option, value) => {
            return option.id === value.id
          }}
          getOptionKey={(option) => option.id || ''}
          getOptionLabel={(option) => option.label || ''}
          value={formik.values.type}
          onChange={(evt, value) => onChangeSelect(evt, 'type', value)}
          onBlur={formik.handleBlur}
          renderInput={(params) => (
            <TextField
              {...params}
              id="type"
              name="type"
              label={t('profile.contacts.form.type')}
              variant="outlined"
              sx={{ mb: 4 }}
              error={formik.touched.type && Boolean(formik.errors.type)}
              helperText={formik.touched.type && formik.errors.type ? String(formik.errors.type) : null}
              inputProps={{
                ...params.inputProps,
              }}
            />
          )}
        />
        <TextInputPhone
          id="phone"
          name="phone"
          label={t('profile.contacts.form.phone')}
          variant="outlined"
          sx={{ flex: 1, mb: 4 }}
          value={formik.values.phone}
          onChange={formik.handleChange}
          error={formik.touched.phone && Boolean(formik.errors.phone)}
          helperText={formik.touched.phone && formik.errors.phone}
          fullWidth
        />
        <TextField
          id="description"
          label={t('profile.contacts.form.description')}
          variant="outlined"
          sx={{ flex: 1 }}
          value={formik.values.description}
          onChange={formik.handleChange}
          error={formik.touched.description && Boolean(formik.errors.description)}
          helperText={formik.touched.description && formik.errors.description}
          fullWidth
          multiline
          maxRows={8}
        />

        <Spacer height={2} />
        <Stack direction="row" justifyContent="space-between">
          <ButtonActions
            onAction={onSubmitHandler}
            actionEnabled={formik.dirty}
            onCancel={onCancelHandler}
            invertButtons
          />
          {contact && (
            <Button variant="outlined" onClick={() => setShowDeleteDialog(true)} startIcon={<DeleteIcon />}>
              {t('global.label.delete')}
            </Button>
          )}
        </Stack>
      </Paper>
      {showDeleteDialog && (
        <DeleteDialog contact={contact!} onClose={() => setShowDeleteDialog(false)} onConfirm={deleteContactHandler} />
      )}
    </>
  )
}

export const ProfileContactsEdit = () => {
  const { accountName: locationName = '', contactId = '' } = useParams()
  const sessionCtx = useSessionContext()
  const [isFormDirty, setIsFormDirty] = useState(false)
  const formStateHandler = useCallback((isDirty: boolean) => setIsFormDirty(isDirty), [])
  const lupId = sessionCtx.state.user!.lupId

  const contact = Hooks.useClientLocationAssociatedContact(locationName, lupId, contactId)

  if (contact.isLoading) {
    return (
      <ContactFormWrapper>
        <Skeleton variant="rectangular" width="100%" height={200} />
      </ContactFormWrapper>
    )
  }

  if (contact.isSuccess && contact.data) {
    return (
      <NavBlocker
        sx={{ px: 3, py: 2, width: 444 }}
        shouldBlock={isFormDirty}
        promptTitle={t('profile.navBlocker.title')}
        promptContent={t('profile.navBlocker.content')}
        goBackText={t('profile.navBlocker.back')}
        proceedText={t('profile.navBlocker.proceed')}
      >
        <ContactFormWrapper>
          <ContactEditForm contact={contact.data} onFormState={formStateHandler} />
        </ContactFormWrapper>
      </NavBlocker>
    )
  }

  return (
    <ContactFormWrapper>
      <Typography>Error loading Contact Form</Typography>
    </ContactFormWrapper>
  )
}

export const ProfileContactsAdd = () => {
  const [isFormDirty, setIsFormDirty] = useState(false)
  const formStateHandler = useCallback((isDirty: boolean) => setIsFormDirty(isDirty), [])

  return (
    <NavBlocker
      sx={{ px: 3, py: 2, width: 444 }}
      shouldBlock={isFormDirty}
      promptTitle={t('profile.navBlocker.title')}
      promptContent={t('profile.navBlocker.content')}
      goBackText={t('profile.navBlocker.back')}
      proceedText={t('profile.navBlocker.proceed')}
    >
      <ContactFormWrapper>
        <ContactEditForm onFormState={formStateHandler} />
      </ContactFormWrapper>
    </NavBlocker>
  )
}
